import { AddBlock, RemoveHollow } from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKBadge,
  CKButton,
  CKContainerProps,
  CKDetailGroup,
  CKLayoutHorizontal,
  cloneChildren,
  includeChildrenByDisplayName,
} from 'clearkit';
import { FC, useState } from 'react';

export type FilterProps = CKContainerProps & {
  onClearFilter: () => void;
  filterCount?: number;
};

export type FilterComposition = {
  HelpText: FC<CKContainerProps>;
  Icon: FC<CKContainerProps>;
  Input: FC<CKContainerProps>;
  Title: FC<CKContainerProps>;
};

export const componentDisplayNames = {
  helpTextDisplayName: 'Filter.HelpText',
  iconDisplayName: 'Filter.Icon',
  inputDisplayName: 'Filter.Input',
  titleDisplayName: 'Filter.Title',
};

const {
  helpTextDisplayName,
  iconDisplayName,
  inputDisplayName,
  titleDisplayName,
} = componentDisplayNames;

export const Filter: FC<FilterProps> & FilterComposition = ({
  children,
  onClearFilter,
  filterCount,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClear = () => {
    handleToggle();
    onClearFilter();
  };

  const FilterContent = () => (
    <CKLayoutHorizontal as="span" className="grow shrink" wrap="nowrap">
      <CKLayoutHorizontal
        as="span"
        className="font-medium text-gray-600"
        gap=".5rem"
        wrap="nowrap"
      >
        {includeChildrenByDisplayName({
          children: children,
          componentDisplayName: iconDisplayName,
        })}
        {includeChildrenByDisplayName({
          children: children,
          componentDisplayName: titleDisplayName,
        })}
      </CKLayoutHorizontal>
      {filterCount ? <CKBadge variant="rounded">{filterCount}</CKBadge> : null}
    </CKLayoutHorizontal>
  );

  return (
    <CKDetailGroup {...rest} isOpen={isOpen} onToggle={handleToggle}>
      {filterCount || isOpen ? (
        <CKDetailGroup.TriggerChevron className="!py-1 [&_span]:flex">
          <FilterContent />
        </CKDetailGroup.TriggerChevron>
      ) : (
        <CKButton
          className="w-full !py-1"
          onClick={handleToggle}
          variant="tertiary"
        >
          <CKLayoutHorizontal className="shrink-0" wrap="nowrap">
            <FilterContent />
            <CKLayoutHorizontal className="shrink-0 text-blue" gap=".25rem">
              <AddBlock /> Add
            </CKLayoutHorizontal>
          </CKLayoutHorizontal>
        </CKButton>
      )}
      <CKDetailGroup.Group className="px-0.5 space-y-2">
        {includeChildrenByDisplayName({
          children: children,
          componentDisplayName: inputDisplayName,
        })}
        {includeChildrenByDisplayName({
          children: children,
          componentDisplayName: helpTextDisplayName,
        })}
        {isOpen ? (
          <CKButton
            leftIcon={<RemoveHollow />}
            onClick={handleClear}
            variant="tertiary"
            variantColor="blue"
          >
            Clear filter
          </CKButton>
        ) : null}
      </CKDetailGroup.Group>
    </CKDetailGroup>
  );
};

Filter.Icon = ({ children, ...rest }) => {
  return (
    <span className="flex items-center" {...rest}>
      {cloneChildren({
        children: children,
        newProps: {
          height: 18,
          width: 18,
        },
      })}
    </span>
  );
};
Filter.Icon.displayName = iconDisplayName;

Filter.Input = ({ className, ...rest }) => {
  return <div {...rest} className={classnames('pt-2', className)} />;
};
Filter.Input.displayName = inputDisplayName;

Filter.Title = ({ children, ...rest }) => {
  return (
    <span className="flex items-center" {...rest}>
      {children}
    </span>
  );
};
Filter.Title.displayName = titleDisplayName;

Filter.HelpText = ({ children, className, ...rest }) => (
  <div {...rest} className={classnames(className, 'text-sm')}>
    {children}
  </div>
);
Filter.HelpText.displayName = 'Filter.HelpText';
